import React, { useContext, useState } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';
import { CreditRequestBatchLineItem } from '@/contexts/credit-request-context-types';

interface CreditRequestInvoiceState {
  addBatchItem: (item: CreditRequestBatchLineItem) => void;
  batchItems: CreditRequestBatchLineItem[];
  clearBatchItems: () => void;
  removeBatchItem: (uuid: string) => void;
  updateBatchItemQuantity: (uuid: string, quantity: number) => void;
}

/**
 * Imperative handle to a credit-request invoice store. Useful when you need
 * to read or mutate state outside of a React render (e.g. inside a callback
 * that fires `getState()` once on submit) without subscribing to changes.
 *
 * @example
 * const storeApi: CreditRequestInvoiceStoreApi = useCreditRequestInvoiceStoreApi();
 * const onSubmit = () => {
 *   const { batchItems } = storeApi.getState();
 *   submit(batchItems);
 * };
 */
export type CreditRequestInvoiceStoreApi = StoreApi<CreditRequestInvoiceState>;

/**
 * Builds a fresh credit-request invoice store. Used internally by
 * `CreditRequestInvoiceStoreProvider`; typically you don't call this directly
 * — mount the provider and use the hooks instead.
 */
export const createCreditRequestInvoiceStore =
  (): CreditRequestInvoiceStoreApi =>
    createStore<CreditRequestInvoiceState>()((set) => ({
      addBatchItem: (item) =>
        set((state) => {
          const filtered = state.batchItems.filter((i) => i.uuid !== item.uuid);
          // a new item goes to the beginning of the list
          return { batchItems: [item, ...filtered] };
        }),
      batchItems: [],
      clearBatchItems: () => set({ batchItems: [] }),
      removeBatchItem: (batchItemUUID) =>
        set((state) => ({
          batchItems: state.batchItems.filter((i) => i.uuid !== batchItemUUID),
        })),
      updateBatchItemQuantity: (batchItemUUID, quantity) =>
        set((state) => ({
          batchItems: state.batchItems.map((i) =>
            i.uuid === batchItemUUID ? { ...i, quantity } : i,
          ),
        })),
    }));

const CreditRequestInvoiceStoreContext =
  React.createContext<CreditRequestInvoiceStoreApi | null>(null);

interface ProviderProps {
  children: React.ReactNode;
  initialBatchItems?: CreditRequestBatchLineItem[];
}

/**
 * Provides an isolated credit-request invoice store instance. Each provider
 * mount gets its own store, so the standalone credit-request flow and the
 * short-pay flow don't share basket state.
 *
 * @param props.children - The subtree that consumes the store via
 *   `useCreditRequestInvoiceStore` / `useCreditRequestInvoiceStoreApi`.
 * @param props.initialBatchItems - Convenience for tests that want to render
 *   the consuming screens with a pre-populated basket. Production callers
 *   should leave this unset.
 *
 * @example
 * // Production: wrap the feature subtree at its root.
 * <CreditRequestInvoiceStoreProvider>
 *   <CreditRequestInvoiceItemsScreen />
 *   <CreditRequestInvoiceDetailsScreen />
 * </CreditRequestInvoiceStoreProvider>
 *
 * @example
 * // Tests: seed the basket so the screen renders with items.
 * <CreditRequestInvoiceStoreProvider initialBatchItems={[batchItem]}>
 *   <CreditRequestInvoiceDetailsScreen ... />
 * </CreditRequestInvoiceStoreProvider>
 */
export const CreditRequestInvoiceStoreProvider: React.FC<ProviderProps> = ({
  children,
  initialBatchItems,
}) => {
  const [store] = useState(() => {
    const api = createCreditRequestInvoiceStore();
    if (initialBatchItems && initialBatchItems.length > 0) {
      api.setState({ batchItems: initialBatchItems });
    }
    return api;
  });
  return (
    <CreditRequestInvoiceStoreContext.Provider value={store}>
      {children}
    </CreditRequestInvoiceStoreContext.Provider>
  );
};

/**
 * Subscribes to a slice of the nearest `CreditRequestInvoiceStoreProvider`'s
 * state and re-renders the calling component when that slice changes. Throws
 * if used outside a provider.
 *
 * @param selector - Pure function that derives the slice the caller cares
 *   about from the full store state.
 * @returns The selected slice. Stable across renders until the slice changes.
 *
 * @example
 * const batchItems = useCreditRequestInvoiceStore((s) => s.batchItems);
 * const addBatchItem = useCreditRequestInvoiceStore((s) => s.addBatchItem);
 * const totalInMicros = useCreditRequestInvoiceStore(selectTotalPriceInMicros);
 */
export const useCreditRequestInvoiceStore = <T,>(
  selector: (state: CreditRequestInvoiceState) => T,
): T => {
  const store = useContext(CreditRequestInvoiceStoreContext);
  if (!store) {
    throw new Error(
      'useCreditRequestInvoiceStore must be used within a CreditRequestInvoiceStoreProvider',
    );
  }
  return useStore(store, selector);
};

/**
 * Returns the imperative store handle from the nearest
 * `CreditRequestInvoiceStoreProvider`. Use this when you need `getState()` /
 * `setState()` inside a callback (e.g. to snapshot the basket on submit)
 * without subscribing to renders. Throws if used outside a provider.
 *
 * @example
 * const storeApi = useCreditRequestInvoiceStoreApi();
 * const onSubmit = () => {
 *   const { batchItems } = storeApi.getState();
 *   onCreditRequestWorkflowComplete(amountInMicros, batchItems);
 * };
 */
export const useCreditRequestInvoiceStoreApi =
  (): CreditRequestInvoiceStoreApi => {
    const store = useContext(CreditRequestInvoiceStoreContext);
    if (!store) {
      throw new Error(
        'useCreditRequestInvoiceStoreApi must be used within a CreditRequestInvoiceStoreProvider',
      );
    }
    return store;
  };

/**
 * Selector that totals the basket price in micros (sum of `quantity *
 * priceInMicros` across every batch item).
 */
export const selectTotalPriceInMicros = (s: CreditRequestInvoiceState) =>
  s.batchItems.reduce(
    (acc, item) => acc + item.quantity * item.priceInMicros,
    0,
  );

/**
 * Selector that totals the basket quantity (sum of `quantity` across every
 * batch item).
 */
export const selectTotalQuantity = (s: CreditRequestInvoiceState) =>
  s.batchItems.reduce((acc, item) => acc + item.quantity, 0);
